import { useState } from 'react'
import './Team.css'

// Mock data
const teamData = {
  plan: {
    name: 'Business',
    seats: { used: 5, total: 10 }
  },
  members: [
    {
      id: 1,
      name: 'Sarah Chen',
      email: 'sarah@acmecorp.com',
      avatar: 'SC',
      role: 'Owner',
      modules: ['payments', 'accounting', 'infrastructure'],
      addedDate: 'Jan 2024'
    },
    {
      id: 2,
      name: 'Mike Johnson',
      email: 'mike@acmecorp.com',
      avatar: 'MJ',
      role: 'Admin',
      modules: ['payments', 'accounting', 'infrastructure'],
      addedDate: 'Mar 2025'
    },
    {
      id: 3,
      name: 'Alex Rivera',
      email: 'alex@acmecorp.com',
      avatar: 'AR',
      role: 'Member',
      modules: ['payments'],
      addedDate: 'Jan 2026'
    },
    {
      id: 4,
      name: 'Emily Watson',
      email: 'emily@acmecorp.com',
      avatar: 'EW',
      role: 'Member',
      modules: ['accounting'],
      addedDate: 'Jan 2026'
    },
    {
      id: 5,
      name: 'David Park',
      email: 'david@acmecorp.com',
      avatar: 'DP',
      role: 'Billing',
      modules: [],
      addedDate: 'Feb 2026'
    }
  ],
  pendingInvites: [
    {
      id: 101,
      email: 'jamie@acmecorp.com',
      role: 'Member',
      modules: ['accounting'],
      invitedDate: '2 days ago'
    }
  ]
}

const roles = [
  { id: 'owner', name: 'Owner', description: 'Full access to everything' },
  { id: 'admin', name: 'Admin', description: 'Full access, view-only billing' },
  { id: 'member', name: 'Member', description: 'Access to assigned modules only' },
  { id: 'billing', name: 'Billing', description: 'Billing access only' }
]

const modules = [
  { id: 'payments', name: 'Payments (Chekkouts)', color: 'payments' },
  { id: 'accounting', name: 'Accounting (Keeply)', color: 'accounting' },
  { id: 'infrastructure', name: 'Infrastructure (Namelyft)', color: 'infrastructure' }
]

function Team() {
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'member',
    modules: []
  })

  const handleInvite = (e) => {
    e.preventDefault()
    // In production, this would call an API
    console.log('Inviting:', inviteData)
    setShowInviteModal(false)
    setInviteData({ email: '', role: 'member', modules: [] })
  }

  const handleEditMember = (member) => {
    setSelectedMember(member)
    setShowEditModal(true)
  }

  const getModuleLabel = (moduleIds) => {
    if (moduleIds.length === 0) return 'No module access'
    if (moduleIds.length === modules.length) return 'All modules'
    return moduleIds.map(id => modules.find(m => m.id === id)?.name.split(' ')[0]).join(', ')
  }

  return (
    <div className="team-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Team</h1>
          <p className="page-subtitle">
            {teamData.plan.seats.used} of {teamData.plan.seats.total} seats used ({teamData.plan.name} plan)
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          Invite team member
        </button>
      </div>

      {/* Members List */}
      <div className="team-card">
        <div className="card-header">
          <h2 className="card-title">Team Members</h2>
          <span className="member-count">{teamData.members.length} members</span>
        </div>
        
        <div className="members-list">
          {teamData.members.map(member => (
            <div key={member.id} className="member-item">
              <div className="member-avatar">{member.avatar}</div>
              <div className="member-info">
                <span className="member-name">{member.name}</span>
                <span className="member-email">{member.email}</span>
              </div>
              <div className="member-role">
                <span className={`role-badge ${member.role.toLowerCase()}`}>{member.role}</span>
              </div>
              <div className="member-modules">
                <span className="modules-label">{getModuleLabel(member.modules)}</span>
              </div>
              <div className="member-date">
                <span className="date-label">Added {member.addedDate}</span>
              </div>
              <div className="member-actions">
                <button 
                  className="action-button"
                  onClick={() => handleEditMember(member)}
                  disabled={member.role === 'Owner'}
                >
                  Manage
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations */}
      {teamData.pendingInvites.length > 0 && (
        <div className="team-card">
          <div className="card-header">
            <h2 className="card-title">Pending Invitations</h2>
          </div>
          
          <div className="members-list">
            {teamData.pendingInvites.map(invite => (
              <div key={invite.id} className="member-item pending">
                <div className="member-avatar pending">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div className="member-info">
                  <span className="member-email">{invite.email}</span>
                  <span className="invite-status">Invitation pending</span>
                </div>
                <div className="member-role">
                  <span className={`role-badge ${invite.role.toLowerCase()}`}>{invite.role}</span>
                </div>
                <div className="member-modules">
                  <span className="modules-label">{getModuleLabel(invite.modules)}</span>
                </div>
                <div className="member-date">
                  <span className="date-label">Invited {invite.invitedDate}</span>
                </div>
                <div className="member-actions">
                  <button className="action-button">Resend</button>
                  <button className="action-button danger">Cancel</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles Reference */}
      <div className="team-card">
        <div className="card-header">
          <h2 className="card-title">Role Permissions</h2>
        </div>
        
        <div className="roles-grid">
          {roles.map(role => (
            <div key={role.id} className="role-card">
              <span className={`role-badge ${role.id}`}>{role.name}</span>
              <p className="role-description">{role.description}</p>
              <ul className="role-permissions">
                {role.id === 'owner' && (
                  <>
                    <li>✓ Full platform access</li>
                    <li>✓ All modules</li>
                    <li>✓ Billing management</li>
                    <li>✓ Team management</li>
                  </>
                )}
                {role.id === 'admin' && (
                  <>
                    <li>✓ Full platform access</li>
                    <li>✓ All modules</li>
                    <li>○ View billing only</li>
                    <li>✓ Team management</li>
                  </>
                )}
                {role.id === 'member' && (
                  <>
                    <li>○ Limited access</li>
                    <li>○ Assigned modules only</li>
                    <li>✗ No billing access</li>
                    <li>✗ No team management</li>
                  </>
                )}
                {role.id === 'billing' && (
                  <>
                    <li>○ Limited access</li>
                    <li>✗ No module access</li>
                    <li>✓ Full billing access</li>
                    <li>✗ No team management</li>
                  </>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Invite Team Member</h2>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleInvite} className="modal-content">
              <div className="form-group">
                <label className="form-label">Email address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="colleague@company.com"
                  value={inviteData.email}
                  onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-select"
                  value={inviteData.role}
                  onChange={e => setInviteData({ ...inviteData, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="billing">Billing</option>
                </select>
              </div>
              
              {inviteData.role === 'member' && (
                <div className="form-group">
                  <label className="form-label">Module access</label>
                  <div className="checkbox-group">
                    {modules.map(module => (
                      <label key={module.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={inviteData.modules.includes(module.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setInviteData({
                                ...inviteData,
                                modules: [...inviteData.modules, module.id]
                              })
                            } else {
                              setInviteData({
                                ...inviteData,
                                modules: inviteData.modules.filter(m => m !== module.id)
                              })
                            }
                          }}
                        />
                        <span className={`module-dot ${module.color}`}></span>
                        {module.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="form-note">
                They'll receive an email to set up their Hubthrill login.
              </p>
              
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Send invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Member: {selectedMember.name}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="member-edit-header">
                <div className="member-avatar large">{selectedMember.avatar}</div>
                <div>
                  <span className="member-name">{selectedMember.name}</span>
                  <span className="member-email">{selectedMember.email}</span>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" defaultValue={selectedMember.role.toLowerCase()}>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="billing">Billing</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Module Access</label>
                <div className="module-access-list">
                  {modules.map(module => (
                    <div key={module.id} className="module-access-item">
                      <label className="switch-label">
                        <input 
                          type="checkbox" 
                          defaultChecked={selectedMember.modules.includes(module.id)}
                        />
                        <span className="switch"></span>
                        <span className={`module-dot ${module.color}`}></span>
                        {module.name}
                      </label>
                      {selectedMember.modules.includes(module.id) && (
                        <div className="access-level">
                          <label className="radio-label">
                            <input type="radio" name={`access-${module.id}`} defaultChecked />
                            Full access
                          </label>
                          <label className="radio-label">
                            <input type="radio" name={`access-${module.id}`} />
                            View only
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="modal-footer">
                <button className="btn btn-danger">Remove from team</button>
                <div className="footer-actions">
                  <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary">
                    Save changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Team
